import { supabase } from "../supabaseClient"

const API_BASE = process.env.REACT_APP_SERVER_URL || "http://localhost:8000"

export interface Campaign {
  campaign_id: string
  name: string
  status: string
  voice_agent_id?: string
  sms_agent_id?: string
  email_agent_id?: string
  whatsapp_agent_id?: string
  sequence_config: any
  created_at: string
  updated_at: string
  company_name?: string
}

export interface EmailExecutionResult {
  success: boolean
  campaign_id: string
  email_sent: boolean
  messageId?: string
  to: string
  subject: string
}

async function getAuthHeaders(isAdmin?: boolean, companyName?: string): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: HeadersInit = { "Content-Type": "application/json" }

  if (session) {
    headers["user-id"] = session.user.id

    // Automatically include company name from localStorage for admin users
    const selectedCompanyName = localStorage.getItem("selectedCompanyName")
    if (selectedCompanyName) {
      headers["x-company-name"] = selectedCompanyName
    }
  }

  return headers
}

export const campaignService = {
  async list(companyName?: string, isAdmin?: boolean): Promise<Campaign[]> {
    const headers = await getAuthHeaders(isAdmin, companyName)
    const url = `${API_BASE}/api/campaigns`

    const response = await fetch(url, { headers })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    return Array.isArray(data) ? data : []
  },

  async get(id: string): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api/campaigns/${id}`, {
      headers,
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `HTTP error! status: ${response.status}`)
    }
    return response.json()
  },

  async create(campaign: Partial<Campaign>, companyName?: string): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const body = companyName ? { ...campaign, company_name: companyName } : campaign

    const response = await fetch(`${API_BASE}/api/campaigns`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${error}`)
    }
    return response.json()
  },

  async update(id: string, campaign: Partial<Campaign>): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api/campaigns/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(campaign),
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  },

  async delete(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api/campaigns/${id}`, {
      method: "DELETE",
      headers,
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  },

  async executeEmail(campaignId: string, prospectEmail: string, prospectData: any = {}): Promise<any> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/execute-email`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        prospect_email: prospectEmail,
        prospect_data: prospectData,
      }),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `HTTP error! status: ${response.status}`)
    }
    return response.json()
  },

  async getVoiceAgentsWithPhones(companyName?: string, isAdmin?: boolean): Promise<{ agents: any[], agentPhoneMap: Record<string, string[]> }> {
    const headers = await getAuthHeaders(isAdmin, companyName)
    const response = await fetch(`${API_BASE}/api/voice-agents-with-phones`, { headers })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  },

  async getSMSAgentsWithPhones(companyName?: string, isAdmin?: boolean): Promise<any[]> {
    const headers = await getAuthHeaders(isAdmin, companyName)
    const response = await fetch(`${API_BASE}/api/sms-agents-with-phones`, { headers })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  },

  async getEmailAgentsWithAddresses(companyName?: string, isAdmin?: boolean): Promise<any[]> {
    const headers = await getAuthHeaders(isAdmin, companyName)
    const response = await fetch(`${API_BASE}/api/email-agents-with-addresses`, { headers })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  },
}
